import { Link } from 'react-router-dom';
import {
  FirstRunGuide,
  HelpTip,
  buttonVariants,
  guideStorageKey,
  useFirstRunGuide,
  type GuideStepDef,
} from '@dataroom/shared';

function GoLink({ to, label, onClick }: { to: string; label: string; onClick?: () => void }) {
  return (
    <Link to={to} className={buttonVariants({ size: 'sm', variant: 'outline' })} onClick={onClick}>
      {label}
    </Link>
  );
}

/**
 * The seller's steps (exported for the tests): drop in documents, see what is missing,
 * find and fix a document, answer buyer questions and — owners only — how LOI voting
 * works. A team member never gets the voting step (they never vote).
 */
export function sellerSteps(input: {
  base: string;
  isOwner: boolean;
  hasDocuments: boolean;
}): GuideStepDef[] {
  const { base, isOwner, hasDocuments } = input;
  const steps: GuideStepDef[] = [
    {
      id: 'documents',
      title: 'Drop in your documents',
      description:
        'No sorting needed. Add files or a whole folder in the box above; each one is read, filed and matched to the checklist for you.',
      done: hasDocuments,
    },
    {
      id: 'missing',
      title: 'See what’s still missing',
      description:
        'Progress and Due soon show what is outstanding, so you know what to look for next.',
      action: (markDone) => (
        <GoLink to={`${base}/checklist`} label="Open the checklist" onClick={markDone} />
      ),
    },
    {
      id: 'tidy',
      title: 'Find a document, or fix where it’s filed',
      description: 'You can always correct the AI.',
      // 'search' and 'manage-document' tick when the person does them.
      parts: [
        { id: 'search', label: 'Search by any word in a name, folder or date' },
        {
          id: 'manage-document',
          label: 'Open a document and choose Manage document to move it or change its date',
        },
      ],
      action: () => <GoLink to={`${base}/documents`} label="Open Documents" />,
    },
    {
      id: 'questions',
      title: 'Answer buyer questions',
      description:
        'Questions for you wait here. Answer in your own words; the deal team shares them.',
      action: (markDone) => (
        <GoLink to={`${base}/questions`} label="Open questions" onClick={markDone} />
      ),
    },
  ];
  if (isOwner) {
    steps.push({
      id: 'voting',
      title: 'How LOI voting works',
      description:
        'When a buyer sends a letter of intent, the owners decide by majority of ownership. Every decision asks “Are you sure?” before it counts.',
      action: (markDone) => <GoLink to={`${base}/lois`} label="Open LOIs" onClick={markDone} />,
    });
  }
  return steps;
}

/** The seller portal's first-visit guide, "Your room in N steps". One per room, remembered per browser. */
export function SellerGuide({
  roomId,
  isOwner,
  hasDocuments,
}: {
  roomId: string;
  isOwner: boolean;
  hasDocuments: boolean;
}) {
  const guide = useFirstRunGuide(guideStorageKey('seller', roomId));
  const steps = sellerSteps({ base: `/rooms/${roomId}`, isOwner, hasDocuments });
  return (
    <FirstRunGuide
      guide={guide}
      title={`Your room in ${steps.length} steps`}
      titleAside={<HelpTip id="seller.dashboard.room-guide" />}
      ariaLabel="Seller guide"
      steps={steps}
      resumeLabel="Show the room guide"
    />
  );
}
